import type { ComponentProps } from "react";
import { t } from "ttag";

import type { CheckoutDiscount } from "@/features/payments/api";
import type { CategoryWithSelectedProduct } from "@/features/products/models";
import { calculateTotal } from "@/features/products/utils";
import { Card, Text } from "@/shared/ui";
import { formatPrice } from "@/shared/utils";

const SummaryRow = ({ ...props }: ComponentProps<"div">) => {
  return <div className="flex items-start justify-between" {...props} />;
};

interface OrderSummaryProps {
  categories: CategoryWithSelectedProduct[];
  checkoutDiscount?: CheckoutDiscount;
  sticky?: boolean;
}

export const OrderSummary = ({
  categories,
  checkoutDiscount,
  sticky = true,
}: OrderSummaryProps) => {
  const total =
    checkoutDiscount?.totalAfterDiscount ?? calculateTotal(categories);

  return (
    <Card.Root
      className={`h-fit w-full flex-col gap-3 rounded-lg px-4 pt-3.5 pb-5 md:p-5 ${sticky ? "md:sticky md:-top-12 md:max-w-80" : ""}`}
    >
      <Card.Title weight="weak">{t`Order summary`}</Card.Title>

      <Card.Separator className="bg-secondary-100" />

      {categories.map(({ selectedProduct }) => {
        if (selectedProduct) {
          const discount = checkoutDiscount?.prices.find(
            (price) =>
              price.subscriptionPriceId ===
              selectedProduct.selectedPrice?.subscriptionPriceId,
          )?.discount;

          return (
            <SummaryRow key={selectedProduct.id}>
              <Text color="light-neutral">{selectedProduct.name}</Text>

              {discount?.isValid ? (
                <div className="flex items-center gap-1.5">
                  <Text
                    size="small"
                    weight="medium"
                    color="light-neutral"
                    lineThrough
                  >
                    {formatPrice(selectedProduct.selectedPrice?.price)}
                  </Text>
                  <Text weight="medium" color="dark-neutral">
                    {formatPrice(discount.priceAfterDiscount)}
                  </Text>
                </div>
              ) : (
                <Text weight="medium" color="dark-neutral">
                  {formatPrice(selectedProduct.selectedPrice?.price)}
                </Text>
              )}
            </SummaryRow>
          );
        }
      })}

      <Card.Separator className="bg-secondary-100" />

      <div className="flex flex-col gap-4 text-lg">
        <SummaryRow>
          <Text weight="bold" color="dark-neutral">{t`Due today`}</Text>

          <Text weight="bold" color="dark-neutral">
            {formatPrice(total)}
          </Text>
        </SummaryRow>

        <Text size="small" color="light-neutral">
          {t`Our guarantee: If you pay for a treatment and your provider determines you're not eligible, we'll issue a full refund`}
        </Text>
      </div>
    </Card.Root>
  );
};
